import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { getAuditTrailByid } from '../../../../../redux/auditTrailSlice'
import { getAllVersionHistories } from '../../../../../redux/versionHistorySlice'
import { openCopyReplace } from '../../../../../redux/previewSlice'
import AppBar from '../../../../../common/AppBar'
import RevisionPreview from '../../MoveAction/RevisionPreview/RevisionPreview'
import Loader from '../../../../../common/Loader'
import VersionHistoryPopup from '../../../../../common/VersionHistoryPopup/VersionHistoryPopup'
import VersionHistoryCopyReplace from '../../../../NodeActions/VersionHistoryCopy/VersionHistoryCopyReplace'
import {
  mergeAuditVersionContent,
  mergeAuditTrailRowVersionContentDeep,
  resolveAuditTrailProcedureIdentity,
  enrichProcedureIdentityFromAuditSnapshots,
} from '../../../utils/auditTrailProcedureIdentity'
import { resolveProcedureIdsFromPartRevisionLabel } from '../../../utils/resolveProcedureIdsFromPartRevisionLabel'

/**
 * Move Preview Layout - UI matches /audit-trail/preview
 * Shows get-by-id data and uses RevisionPreview for content (ProcedureStatusInfo, View Original/View New, snapshot content)
 */
const MovePreviewLayout = () => {
  const dispatch = useDispatch()
  const { audit_trail_id } = useParams()
  const navigate = useNavigate()
  const [revData, setRevData] = useState({ version_content: {} })
  /** Part/rev from S3 new_content_snapshot / previous_content_snapshot when get-by-id omits them (bulk SECTION_MOVED, etc.). */
  const [snapshotEnrichedIdentity, setSnapshotEnrichedIdentity] = useState(null)
  const [showVersionHistory, setShowVersionHistory] = useState(false)

  const { loading } = useSelector(state => state.audittrail)
  const { isOpen: isCopyReplaceOpen } = useSelector(state => state.preview)
  const { versionHistories } = useSelector((state) => state.versionHistory)

  const handleTablesVersionHistory = useCallback(async () => {
    const vid = revData?.version_id
    if (!vid) return
    await dispatch(getAllVersionHistories({ versionId: vid }))
    setShowVersionHistory(true)
  }, [dispatch, revData?.version_id])

  useEffect(() => {
    (async () => {
      const resp = await dispatch(getAuditTrailByid({ id: audit_trail_id }))
      const auditData = resp.payload?.results ?? resp.payload?.data ?? resp.payload
      if (auditData && typeof auditData === 'object') setRevData(auditData)
    })()
  }, [dispatch, audit_trail_id])

  useEffect(() => {
    let cancelled = false
    setSnapshotEnrichedIdentity(null)
    const hasUrl = revData?.new_content_snapshot || revData?.previous_content_snapshot
    if (!hasUrl) return () => { cancelled = true }
    ;(async () => {
      const base = resolveAuditTrailProcedureIdentity(revData)
      const enriched = await enrichProcedureIdentityFromAuditSnapshots(revData, base)
      if (!cancelled) setSnapshotEnrichedIdentity(enriched)
    })()
    return () => { cancelled = true }
  }, [audit_trail_id, revData?.new_content_snapshot, revData?.previous_content_snapshot])

  // Use current audit entry's version_content for title (not parent), so "Section 1" shows for section moves
  const vc = revData.version_content?.version_content || revData.version_content || {}
  const parentVc = revData.parent_version_data?.version_content || {}
  const action = (vc.action_to_show || '').toUpperCase()
  const actionMessage = typeof revData.action === 'string' ? revData.action : ''

  const getSectionLabel = (src) => src?.section || (typeof src?.breadcrumb_text === 'string' && (() => { const parts = src.breadcrumb_text.split(' > '); return parts.find(p => p && p.match(/^Section\s+\d+$/i)); })()) || (src?.pageTitle && src.pageTitle.match(/^Section\s+\d+$/i) ? src.pageTitle : null) || (src?.sibling_order != null ? `Section ${src.sibling_order}` : null)

  const getDisplayTitleBase = () => {
    const msg = (actionMessage || '').trim()
    // Primary title from message start: "User Moved/Copied Step N into|above|from ..." -> "Step N" (same for Substep/Section) even when action_to_show is child op (e.g. VIDEO_MOVED)
    const userMovedStep = msg.match(/^User\s+(?:Moved|Copied)\s+(Step\s+\d+)(?:\s+into|\s+above|\s+from)/i)
    if (userMovedStep) return userMovedStep[1]
    const userMovedSubstep = msg.match(/^User\s+(?:Moved|Copied)\s+(Substep\s+[\dA-Za-z]+)(?:\s+into|\s+above|\s+from)/i)
    if (userMovedSubstep) return userMovedSubstep[1]
    // Fallback: "User Copied Substep 1B from Step 1 of Section 1 in X, into ..."
    const userSubstepAlt = msg.match(/^User\s+(?:Moved|Copied)\s+(Substep\s+[\dA-Za-z]+)\s+from/i)
    if (userSubstepAlt) return userSubstepAlt[1]
    const userMovedSection = msg.match(/^User\s+(?:Moved|Copied)\s+(Section\s+\d+)(?:\s+into|\s+above|\s+from)/i)
    if (userMovedSection) return userMovedSection[1]
    const userDeleted = msg.match(/^(?:User\s+)?Deleted\s+(Section\s+\d+|Step\s+\d+|Substep\s+[\dA-Za-z]+)/i)
    if (userDeleted) return userDeleted[1]
    // Deleted child node: "Deleted Attachment 3 from ..." or "User Deleted Video 2 from ..." (optional "User ")
    const deletedAttachment = msg.match(/^(?:User\s+)?Deleted\s+(Attachment\s+\d+)(?:\s+from)?/i)
    if (deletedAttachment) return deletedAttachment[1]
    const deletedVideo = msg.match(/^(?:User\s+)?Deleted\s+(Video\s+\d+)(?:\s+from)?/i)
    if (deletedVideo) return deletedVideo[1]
    const deletedCanvas = msg.match(/^(?:User\s+)?Deleted\s+(Canvas\s+\d+)(?:\s+from)?/i)
    if (deletedCanvas) return deletedCanvas[1]
    const deletedRichText = msg.match(/^(?:User\s+)?Deleted\s+(Rich\s+Text\s+\d+)(?:\s+from)?/i)
    if (deletedRichText) return deletedRichText[1]
    // Child node move/copy: "User Moved Attachment 1..." or "Moved Rich Text 2 from..." (optional "User ") -> show "Attachment 1" / "Rich Text 2"
    const userMovedAttachment = msg.match(/^(?:User\s+)?(?:Moved|Copied)\s+(Attachment\s+\d+)(?:\s+from|\s+into)/i)
    if (userMovedAttachment) return userMovedAttachment[1]
    const userMovedVideo = msg.match(/^(?:User\s+)?(?:Moved|Copied)\s+(Video\s+\d+)(?:\s+from|\s+into)/i)
    if (userMovedVideo) return userMovedVideo[1]
    const userMovedCanvas = msg.match(/^(?:User\s+)?(?:Moved|Copied)\s+(Canvas\s+\d+)(?:\s+from|\s+into)/i)
    if (userMovedCanvas) return userMovedCanvas[1]
    const userMovedRichText = msg.match(/^(?:User\s+)?(?:Moved|Copied)\s+(Rich\s+Text\s+\d+)(?:\s+from|\s+into)/i)
    if (userMovedRichText) return userMovedRichText[1]

    const sectionLabel = getSectionLabel(vc) || getSectionLabel(parentVc)
    const hasSectionContext = (vc.section_tree_node_id || parentVc.section_tree_node_id) && (vc.section || parentVc.section || (typeof (vc.breadcrumb_text || parentVc.breadcrumb_text) === 'string' && (vc.breadcrumb_text || parentVc.breadcrumb_text).split(' > ').some(p => p && p.match(/^Section\s+\d+$/i))))

    // When message says "User Moved Step 2" (or "Move Step 2 in Section"), show "Step 2" even if action_to_show includes SUBSTEP
    if (action.includes('STEP') && (actionMessage.match(/User\s+Moved\s+Step\s+\d+/i) || actionMessage.match(/Move\s+Step\s+\d+\s+in\s+Section/i))) {
      const stepFromMsg = actionMessage.match(/(?:User\s+)?(?:Moved|Copied)\s+(Step\s+\d+)/i)?.[1] || actionMessage.match(/Move\s+(Step\s+\d+)\s+in\s+Section/i)?.[1]
      if (stepFromMsg) return stepFromMsg
    }

    // Step move/copy: show "Step 2" - use only stepTitle or pageTitle that matches "Step N", not "Substep 1A". Prefer message when it says "User Moved Step 2".
    if (action.includes('STEP') && !action.includes('SUBSTEP')) {
      const stepFromMsg = actionMessage.match(/(?:User\s+)?(?:Moved|moved|Copied|copied)\s+(Step\s+\d+)/i)?.[1] || actionMessage.match(/Move\s+(Step\s+\d+)\s+in\s+Section/i)?.[1] || actionMessage.match(/(Step\s+\d+)/i)?.[1]
      if (stepFromMsg) return stepFromMsg
      const stepOnly = (s) => (s && typeof s === 'string' && s.match(/^Step\s+\d+$/i)) ? s : null
      const stepLabel = vc.stepTitle || parentVc.stepTitle || stepOnly(vc.pageTitle) || stepOnly(parentVc.pageTitle)
      if (stepLabel) return stepLabel
      return 'Step'
    }
    if (action.includes('SUBSTEP')) {
      const substepOnly = (s) => (s && typeof s === 'string' && s.match(/^Substep\s+[\dA-Za-z]+$/i)) ? s : null
      const substepLabel = vc.substepTitle || parentVc.substepTitle || substepOnly(vc.pageTitle) || substepOnly(parentVc.pageTitle)
      if (substepLabel) return substepLabel
      const substepFromMsg = actionMessage.match(/(?:Moved|moved|Copied|copied)\s+(Substep\s+[\dA-Za-z]+)/i)?.[1] || actionMessage.match(/(Substep\s+[\dA-Za-z]+)/i)?.[1]
      if (substepFromMsg) return substepFromMsg
      return 'Substep'
    }
    if (action.includes('SECTION') || hasSectionContext) {
      if (sectionLabel) return sectionLabel
      if (hasSectionContext) return 'Section'
    }
    if (action.includes('DELETE')) {
      if (sectionLabel) return sectionLabel
      const stepLabel = vc.stepTitle || parentVc.stepTitle || vc.pageTitle || parentVc.pageTitle
      if (stepLabel && (stepLabel.match(/^Step\s+\d+$/i) || (action.includes('STEP') && !action.includes('SUBSTEP')))) return stepLabel
      const substepLabel = vc.substepTitle || parentVc.substepTitle || vc.pageTitle || parentVc.pageTitle
      if (substepLabel && (substepLabel.match(/^Substep\s+[\dA-Za-z]+$/i) || action.includes('SUBSTEP'))) return substepLabel
      if (vc.pageTitle || vc.elementTitle || vc.name || parentVc.pageTitle || parentVc.elementTitle || parentVc.name) return vc.pageTitle || vc.elementTitle || vc.name || parentVc.pageTitle || parentVc.elementTitle || parentVc.name
      const sectionFromMsg = actionMessage.match(/(?:Deleted|deleted)\s+(Section\s+\d+)/i)?.[1] || actionMessage.match(/(Section\s+\d+)/i)?.[1]
      if (sectionFromMsg) return sectionFromMsg
      const stepFromMsg = actionMessage.match(/(?:Deleted|deleted)\s+(Step\s+\d+)/i)?.[1] || actionMessage.match(/(Step\s+\d+)/i)?.[1]
      if (stepFromMsg) return stepFromMsg
      const substepFromMsg = actionMessage.match(/(?:Deleted|deleted)\s+(Substep\s+[\dA-Za-z]+)/i)?.[1] || actionMessage.match(/(Substep\s+[\dA-Za-z]+)/i)?.[1]
      if (substepFromMsg) return substepFromMsg
    }
    // Video, Attachment, Rich Text, Canvas (edit/move/copy) - use pageTitle/elementTitle/name; fallback to parent for edit entries
    const isChildNode = action.includes('VIDEO') || action.includes('ATTACHMENT') || action.includes('RICH_TEXT') || action.includes('RICH-TEXT') || action.includes('CANVAS') || action.includes('CANVA') || ['video', 'attachment', 'rich-text', 'canvas'].includes(vc.type)
    if (isChildNode) {
      const label = vc.pageTitle || vc.elementTitle || vc.name || parentVc.pageTitle || parentVc.elementTitle || parentVc.name ||
        (typeof vc.breadcrumb_text === 'string' ? vc.breadcrumb_text.split(' > ').pop() : null) ||
        (typeof parentVc.breadcrumb_text === 'string' ? parentVc.breadcrumb_text.split(' > ').pop() : null)
      if (label) return label
      const typeLabel = vc.type === 'canva' ? 'Canvas' : (vc.type || '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      return typeLabel || 'Item'
    }
    return (
      vc.pageTitle ||
      vc.elementTitle ||
      vc.name ||
      (typeof vc.breadcrumb_text === 'string' ? vc.breadcrumb_text.split(' > ').pop() : null) ||
      vc.stepTitle ||
      vc.section ||
      'Item'
    )
  }

  const displayTitleBase = getDisplayTitleBase()

  const procedureIdentityForUI =
    snapshotEnrichedIdentity ?? resolveAuditTrailProcedureIdentity(revData)

  // Build revision-submission params from an audit object (revData or fresh fetch)
  const getCancelParams = (audit, identityOverride) => {
    const id = identityOverride ?? resolveAuditTrailProcedureIdentity(audit)
    return {
      partId: id.partId,
      revision: id.revision || undefined,
      partNumber: id.partNumber || undefined,
      revisionId: id.revisionId,
    }
  }

  const displayTitle = revData.version_id
    ? `${displayTitleBase} (Version ${revData.version_id}) Selected`
    : 'Loading...'

  const handleReviewClick = () => {
    navigate(`/audit-trail/move-copy-delete-preview/${audit_trail_id}`)
  }

  const handleViewAuditTrail = () => {
    navigate(`/audit-trail/${audit_trail_id}`)
  }

  const handleCancel = async () => {
    try {
      let partId, revision, partNumber, revisionId
      let auditRowForSavedAt = revData
      let params = getCancelParams(revData, procedureIdentityForUI)
      partId = params.partId
      revision = params.revision
      partNumber = params.partNumber
      revisionId = params.revisionId

      if ((!partId || !revisionId || revision == null || !partNumber) && audit_trail_id) {
        const resp = await dispatch(getAuditTrailByid({ id: audit_trail_id }))
        const isFulfilled = resp?.type?.endsWith?.('/fulfilled')
        const payload = isFulfilled ? resp?.payload : null
        const fresh = payload && (payload.results ?? payload.data ?? payload)
        if (fresh && typeof fresh === 'object' && !('message' in fresh && 'stack' in fresh)) {
          auditRowForSavedAt = fresh
          const freshId = await enrichProcedureIdentityFromAuditSnapshots(
            fresh,
            resolveAuditTrailProcedureIdentity(fresh)
          )
          params = getCancelParams(fresh, freshId)
          partId = partId || params.partId
          revision = revision ?? params.revision
          partNumber = partNumber || params.partNumber
          revisionId = revisionId || params.revisionId
        }
      }

      if ((!partId || !revisionId) && partNumber && revision != null) {
        const resolved = await resolveProcedureIdsFromPartRevisionLabel(dispatch, {
          partId,
          revisionId,
          partNumber,
          revision,
        })
        partId = resolved.partId
        revisionId = resolved.revisionId
      }

      if (partId && revision != null && partNumber && revisionId) {
        sessionStorage.setItem('fromAuditTrail', 'true')
        if (audit_trail_id) {
          sessionStorage.setItem('auditTrailBackAuditTrailId', audit_trail_id)
        }
        const auditTrailSavedAtIso =
          auditRowForSavedAt?.version_created_at ||
          auditRowForSavedAt?.audit_created_at ||
          auditRowForSavedAt?.audit_date ||
          null
        if (auditTrailSavedAtIso) {
          sessionStorage.setItem('auditTrailSavedAtIso', String(auditTrailSavedAtIso))
        } else {
          sessionStorage.removeItem('auditTrailSavedAtIso')
        }
        const newContentSnapshotUrl = auditRowForSavedAt?.new_content_snapshot
          ? String(auditRowForSavedAt.new_content_snapshot)
          : null
        if (newContentSnapshotUrl) {
          sessionStorage.setItem(
            'auditTrailNewContentSnapshotUrl',
            newContentSnapshotUrl,
          )
        } else {
          sessionStorage.removeItem('auditTrailNewContentSnapshotUrl')
        }

        const state = {
              fromAuditTrail: true,
              ...(audit_trail_id ? { auditTrailBackId: audit_trail_id } : {}),
              ...(auditTrailSavedAtIso
                ? { auditTrailSavedAtIso: String(auditTrailSavedAtIso) }
                : {}),
              ...(newContentSnapshotUrl
                ? { auditTrailNewContentSnapshotUrl: newContentSnapshotUrl }
                : {}),
            }

        return;
        navigate(
          `/revision-submission/${partId}/${encodeURIComponent(revision)}/type/${encodeURIComponent(partNumber)}/${revisionId}/redirect`,
          {
            state: state,
          }
        )
      } else {
        navigate(-1)
      }
    } catch (err) {
      console.error('[MovePreviewLayout] handleCancel error:', err)
      navigate(-1)
    }
  }

  const handleCopy = async () => {
    // Extract breadcrumb text for copy operation
    let breadcrumbText = null;
    const vc = revData?.version_content || {};
    
    // Try to extract breadcrumb from version_content
    breadcrumbText = vc.breadcrumb_text;
    
    // If breadcrumbText is still undefined, try to extract it from short_description HTML content
    if (!breadcrumbText && vc?.short_description) {
      const htmlMatch = vc.short_description.match(/(?:Section|Step|Substep)\s+\d+(?:\s*&gt;\s*(?:Section|Step|Substep)\s+\d+)*(?:\s*&gt;\s*Video\s+\d+)?/i);
      if (htmlMatch) {
        breadcrumbText = htmlMatch[0].replace(/&gt;/g, '>');
      }
    }
    
    // If still no breadcrumbText, try to use globally stored breadcrumb from RevisionPreview
    if (!breadcrumbText && window.lastExtractedBreadcrumb) {
      breadcrumbText = window.lastExtractedBreadcrumb;
    }
    
    const versionContent =
      mergeAuditTrailRowVersionContentDeep(revData) ||
      mergeAuditVersionContent(revData) ||
      revData.version_content ||
      {}
    const action = (versionContent.action_to_show || '').toUpperCase()

    let nodeType = null
    let sourceNode = {}
    let sourceDocument = {}

    let identity = snapshotEnrichedIdentity ?? resolveAuditTrailProcedureIdentity(revData)
    if (
      !identity.partId ||
      !identity.revisionId ||
      !identity.partNumber ||
      !identity.revision
    ) {
      identity = await enrichProcedureIdentityFromAuditSnapshots(revData, identity)
    }
    const pr = revData.procedure_revision
    const finalPartNumber = identity.partNumber || pr?.part_number || pr?.partNumber || ''
    const finalRevision =
      (identity.revision ||
        pr?.revision ||
        pr?.rev ||
        pr?.part_rev ||
        pr?.label) ??
      ''
    const finalPartId =
      identity.partId ||
      pr?.part_id_ref ||
      pr?.partId ||
      pr?.part_ref
    const finalRevisionId =
      identity.revisionId ||
      pr?.id ||
      pr?.revision_id ||
      pr?.procedure_revision_id

    sourceDocument = {
      partId: finalPartId,
      revisionId: finalRevisionId,
      partNumber: finalPartNumber,
      revision: finalRevision,
      type: versionContent.item_code_type || versionContent.type || revData.procedure_revision?.item_code_type || 'Procedure',
      description: versionContent.partDescription || versionContent.description || versionContent.short_description || revData.procedure_revision?.description || '',
    }

    let copyPartId = sourceDocument.partId
    let copyRevisionId = sourceDocument.revisionId
    let copyPartNumber = sourceDocument.partNumber
    let copyRevision = sourceDocument.revision

    if (
      (!copyPartId ||
        !copyRevisionId ||
        copyRevision == null ||
        copyPartNumber === '') &&
      audit_trail_id
    ) {
      const resp = await dispatch(getAuditTrailByid({ id: audit_trail_id }))
      const isFulfilled = resp?.type?.endsWith?.('/fulfilled')
      const payload = isFulfilled ? resp?.payload : null
      const fresh = payload && (payload.results ?? payload.data ?? payload)
      if (
        fresh &&
        typeof fresh === 'object' &&
        !('message' in fresh && 'stack' in fresh)
      ) {
        let freshIdentity = resolveAuditTrailProcedureIdentity(fresh)
        if (
          !freshIdentity.partId ||
          !freshIdentity.revisionId ||
          !freshIdentity.partNumber ||
          !freshIdentity.revision
        ) {
          freshIdentity = await enrichProcedureIdentityFromAuditSnapshots(
            fresh,
            freshIdentity
          )
        }
        const prFresh = fresh.procedure_revision
        copyPartId =
          copyPartId ||
          freshIdentity.partId ||
          prFresh?.part_id_ref ||
          prFresh?.partId ||
          prFresh?.part_ref
        copyRevisionId =
          copyRevisionId ||
          freshIdentity.revisionId ||
          prFresh?.id ||
          prFresh?.revision_id ||
          prFresh?.procedure_revision_id
        copyPartNumber =
          copyPartNumber ||
          freshIdentity.partNumber ||
          prFresh?.part_number ||
          prFresh?.partNumber ||
          ''
        copyRevision =
          copyRevision ||
          freshIdentity.revision ||
          prFresh?.revision ||
          prFresh?.rev ||
          prFresh?.part_rev ||
          prFresh?.label ||
          ''
      }
    }

    if (
      (!copyPartId || !copyRevisionId) &&
      copyPartNumber &&
      copyRevision != null
    ) {
      const resolved = await resolveProcedureIdsFromPartRevisionLabel(dispatch, {
        partId: copyPartId,
        revisionId: copyRevisionId,
        partNumber: copyPartNumber,
        revision: copyRevision,
      })
      copyPartId = copyPartId || resolved.partId
      copyRevisionId = copyRevisionId || resolved.revisionId
    }

    sourceDocument = {
      ...sourceDocument,
      partId: copyPartId,
      revisionId: copyRevisionId,
      partNumber: copyPartNumber,
      revision: copyRevision,
    }

    if (action.includes('SECTION')) {
      nodeType = 'section'
      let sectionTitle = 'Section'
      if (versionContent.breadcrumb_text) {
        const breadcrumbParts = versionContent.breadcrumb_text.split(' > ')
        const lastPart = breadcrumbParts[breadcrumbParts.length - 1]
        if (lastPart && lastPart.match(/^Section\s+\d+$/i)) sectionTitle = lastPart
      } else if (versionContent.sibling_order) {
        sectionTitle = `Section ${versionContent.sibling_order}`
      } else if (versionContent.pageTitle && versionContent.pageTitle.match(/^Section\s+\d+$/i)) {
        sectionTitle = versionContent.pageTitle
      }
      // Same procedure-tree id chain as PreviewLayout — audit often stores section tree id on
      // tree_node_id / preview_tree_node_id / procedure_tree_ref, not only section_*_ref.
      const sectionProcedureId =
        versionContent.section_tree_node_id ||
        versionContent.section_id_ref ||
        versionContent.tree_node_id ||
        versionContent.preview_tree_node_id ||
        versionContent.procedure_tree_ref
      sourceNode = {
        nodeId: sectionProcedureId,
        nodeTitle: sectionTitle,
        nodeType: 'section',
        sectionId: sectionProcedureId,
      }
    } else if (action.includes('STEP') && !action.includes('SUBSTEP')) {
      nodeType = 'step'
      const stepProcedureId =
        versionContent.step_tree_node_id ||
        versionContent.step_id_ref ||
        versionContent.tree_node_id ||
        versionContent.preview_tree_node_id
      sourceNode = {
        nodeId: stepProcedureId,
        nodeTitle: versionContent.stepTitle || versionContent.pageTitle || 'Step',
        nodeType: 'step',
        sectionId:
          versionContent.section_tree_node_id ||
          versionContent.section_id_ref ||
          versionContent.procedure_tree_ref,
        stepId: stepProcedureId,
      }
    } else if (action.includes('SUBSTEP')) {
      nodeType = 'substep'
      const substepProcedureId =
        versionContent.sub_step_tree_node_id ||
        versionContent.substep_tree_node_id ||
        versionContent.tree_node_id ||
        versionContent.preview_tree_node_id
      const sectionForSubstepId =
        versionContent.section_tree_node_id ||
        versionContent.section_id_ref ||
        versionContent.procedure_tree_ref
      const stepForSubstepId =
        versionContent.step_id_ref || versionContent.step_tree_node_id
      sourceNode = {
        nodeId: substepProcedureId,
        nodeTitle: versionContent.substepTitle || versionContent.pageTitle || 'Substep',
        nodeType: 'substep',
        sectionId: sectionForSubstepId,
        stepId: stepForSubstepId,
        substepId: substepProcedureId,
      }
    } else {
      const nodeTypes = [
        ['RICHTEXT', 'rich-text'],
        ['RICH_TEXT', 'rich-text'],
        ['RICH-TEXT', 'rich-text'],
        ['ATTACHMENT', 'attachment'],
        ['VIDEO', 'video'],
        ['CANVAS', 'canvas'],
        ['CANVA', 'canvas'],
        ['FORM_COPIED', 'form'],
        ['FORM_MOVED', 'form'],
        ['FORM_EDITED', 'form'],
        ['FORM_DELETED', 'form'],
        ['FORM_', 'form'],
      ]
      for (const [key, value] of nodeTypes) {
        if (action.includes(key)) {
          nodeType = value
          break
        }
      }
      if (!nodeType) {
        const raw = (versionContent.type || versionContent.element_type || '')
          .toLowerCase()
          .replace(/_/g, '-')
        const fromType = {
          attachment: 'attachment',
          video: 'video',
          'rich-text': 'rich-text',
          richtext: 'rich-text',
          form: 'form',
          canvas: 'canvas',
          canva: 'canvas',
        }[raw]
        if (fromType) nodeType = fromType
      }
      sourceNode = {
        nodeId:
          versionContent.video_tree_node_id ||
          versionContent.canvas_tree_node_id ||
          versionContent.canva_tree_node_id ||
          versionContent.rich_text_node_id ||
          versionContent.rich_text_tree_node_id ||
          versionContent.attachment_tree_node_id ||
          versionContent.form_tree_node_id ||
          versionContent.tree_node_id ||
          versionContent.preview_tree_node_id ||
          versionContent.element_id,
        nodeTitle: versionContent.name || versionContent.elementTitle || versionContent.pageTitle || 'Element',
        nodeType: nodeType,
        sectionId:
          versionContent.section_tree_node_id ||
          versionContent.section_id_ref ||
          versionContent.procedure_tree_ref,
        stepId:
          versionContent.step_id_ref ||
          versionContent.step_tree_node_id ||
          versionContent.preview_tree_node_id,
        substepId:
          versionContent.substep_id_ref ||
          versionContent.sub_step_tree_node_id ||
          versionContent.substep_tree_node_id,
      }
    }

    // Same label as this screen's AppBar (e.g. "Step 1" from action message) — matches document /node flow
    sourceNode = {
      ...sourceNode,
      nodeTitle: displayTitleBase || sourceNode.nodeTitle,
    }

    dispatch(openCopyReplace({
      copyMode: 'node',
      sourceDocument,
      sourceNode,
      isVersionHistory: true,
      auditTrailId: audit_trail_id,
      versionId: revData.version_id,
      breadcrumbText,
    }))
  }

  return (
    <>
      <AppBar
        title={displayTitle}
        titleFontSize="18px"
        showTablesDropdown={true}
        versionHistoryHandler={
          revData?.version_id ? handleTablesVersionHistory : null
        }
        showCancelButton={true}
        onCancel={handleCancel}
        showBackButton={false}
        showProfileButton={false}
        buttons={[
          { label: 'View Audit Trail Entry', onClick: handleViewAuditTrail },
          { label: 'Review', onClick: handleReviewClick },
          { label: 'Copy', onClick: handleCopy },
        ]}
      />

      {loading ? (
        <Loader />
      ) : (
        <RevisionPreview
          hideAppBar={true}
          externalAuditTrailData={revData}
          procedureAppBarTitleBase={displayTitleBase}
        />
      )}

      {isCopyReplaceOpen && (
        <VersionHistoryCopyReplace
          auditTrailData={revData}
          auditTrailId={audit_trail_id}
          breadcrumbText={(() => {
            // Extract breadcrumb from revData version_content
            const vc = revData?.version_content || {};
            let text = vc.breadcrumb_text;
            
            // If breadcrumbText is still undefined, try to extract it from short_description HTML content
            if (!text && vc?.short_description) {
              const htmlMatch = vc.short_description.match(/(?:Section|Step|Substep)\s+\d+(?:\s*&gt;\s*(?:Section|Step|Substep)\s+\d+)*(?:\s*&gt;\s*Video\s+\d+)?/i);
              if (htmlMatch) {
                text = htmlMatch[0].replace(/&gt;/g, '>');
              }
            }
            
            // If still no text, try to use globally stored breadcrumb from RevisionPreview
            if (!text && window.lastExtractedBreadcrumb) {
              text = window.lastExtractedBreadcrumb;
            }
            
            return text;
          })()}
        />
      )}

      {showVersionHistory && (
        <VersionHistoryPopup
          // isOpen={showVersionHistory}
          onClose={() => setShowVersionHistory(false)}
          versionHistories={versionHistories || []}
          mode="edit"
          versionId={revData.version_id}
          nodeId={
            revData.version_content?.element_id ||
            revData.version_content?.step_tree_node_id
          }
          nodeIdField="element_id"
          breadcrumbText={revData.version_content?.breadcrumb_text}
          // title="Select Version for Comparison"
          // headerStyle={{ color: "#0D6EFD" }}
          comparisonSelect
          initialFilterType="all"
          currentVersionId={revData.version_id}
          onViewDetails={(selectedVersion) => {
            setShowVersionHistory(false)
            navigate(
              `/audit-trail/comparison/${audit_trail_id}?compareWith=${selectedVersion.version_id}`,
            )
          }}
        />
      )}
    </>
  )
}

export default MovePreviewLayout
